import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { OctagonAlert, TriangleAlert } from "lucide-react";
import { supabase } from "@/lib/supabase";
import type { Tone } from "@/components/ui";

// =============================================================================
// standdownShared — HSE stand-downs and carrier embargoes (mig 425, 8 Oct 2026)
// =============================================================================
// James, 8 Oct 2026: "Stand down should be a HSE workflow into the project and
// stop scheduling on that site as the site / client and carrier have stopped
// works, should flag other sites with the client and or same carrier, can be
// also a carrier EMBARGO with dates … so schedule can see it and not plan
// works during one."
//
// The RULE lives in the database (app.standdown_blocking + the booking guards
// on crew bookings, roster rows, project bookings and start cards; ad-hoc
// tasks are NOT refused — mig 426, James: "maybe they are doing a dump run or
// submitting docs back in the office but not on site"). Nothing in this file decides whether a booking is allowed — it only
// SHOWS what the database will say:
//   useOpenStanddowns()        every stop in force or still to come, plus which
//                              open projects each one touches (blocks / flags)
//   stopsForProject()          the stops on one project, blocking ones first
//   stopCovering()             is this booking's date range inside a stop?
//   <ProjectStanddownBanner>   the banner on the project page, the field job
//                              page and the site folder — red when work is
//                              stopped here, amber for a sister-site flag
//   <ScheduleStanddownStrip>   the line on the schedule under the holidays, so
//                              nobody plans into an embargo
// NOT the program line kind "standdown" (mig 371 — a paid no-progress day) and
// NOT a planning block (mig 401 — the office's pencil line).
// =============================================================================

export type StandScope = "project" | "carrier";
export type StandStatus = "scheduled" | "in_force" | "cleared" | "ended";
export type StandRelation = "this_project" | "carrier_embargo" | "same_client" | "same_carrier" | "same_client_and_carrier";

export type Standdown = {
  standdown_id: string;
  standdown_number: string;
  scope: StandScope;
  project_id: string | null;
  project_code: string | null;
  project_name: string | null;
  site_name: string | null;
  client_id: string | null;
  client_name: string | null;
  carrier_id: string | null;
  carrier_name: string | null;
  stopped_by: "client" | "carrier" | "coretel";
  stopped_by_name: string | null;
  reason_kind: "stop_work" | "change_freeze" | "other";
  reason: string;
  notified_via: "alert" | "email" | "meeting" | "phone" | "other";
  alert_id: string | null;
  alert_number: string | null;
  source_url: string | null;
  date_from: string;
  date_to: string | null;
  conditions_to_resume: string | null;
  incident_id: string | null;
  incident_number: string | null;
  raised_by_name: string | null;
  raised_at: string;
  clearance_id: string | null;
  cleared_on: string | null;
  cleared_by_name: string | null;
  cleared_by_org: string | null;
  oversight_condition: string | null;
  clearance_source_url: string | null;
  clearance_note: string | null;
  clearance_recorded_by_name: string | null;
  clearance_recorded_at: string | null;
  block_to: string | null;       // last blocked day; null = until cleared
  status: StandStatus;
  is_open: boolean;
};

export type StandFlag = { standdown_id: string; project_id: string; relation: StandRelation; blocks: boolean };

export const STAND_STATUS: Record<StandStatus, { label: string; tone: Tone }> = {
  in_force:  { label: "In force",  tone: "critical" },
  scheduled: { label: "Coming up", tone: "warning" },
  cleared:   { label: "Cleared",   tone: "ok" },
  ended:     { label: "Ended",     tone: "closed" },
};

export const STOPPED_BY: Record<Standdown["stopped_by"], string> = { client: "The client", carrier: "The carrier", coretel: "Coretel" };
export const REASON_KIND: Record<Standdown["reason_kind"], string> = { stop_work: "Stop-work", change_freeze: "Change freeze", other: "Other" };
export const NOTIFIED_VIA: Record<Standdown["notified_via"], string> = { alert: "HSE alert", email: "Email", meeting: "Meeting", phone: "Phone call", other: "Other" };

// Local day key — never toISOString().slice(0,10) (it shifts the day in AEST).
export function localKey(d: Date = new Date()): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
export function fmtDay(key?: string | null): string {
  if (!key) return "—";
  const [y, m, d] = key.slice(0, 10).split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" });
}
export function fmtStamp(iso?: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-AU", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

/** "02 Oct 2026 → until cleared" / "20 Dec 2026 → 05 Jan 2027" — the days bookings are refused. */
export function blockedRange(s: Pick<Standdown, "date_from" | "block_to">): string {
  return `${fmtDay(s.date_from)} → ${s.block_to ? fmtDay(s.block_to) : "until cleared"}`;
}

/** What a stop is on, in a few words: "NBN — every project" or "P-1234 · Mount Tamborine". */
export function stopSubject(s: Standdown): string {
  if (s.scope === "carrier") return `${s.carrier_name ?? "Carrier"} — every project`;
  return [s.project_code, s.site_name ?? s.project_name].filter(Boolean).join(" · ");
}

export const RELATION_WORDS: Record<StandRelation, string> = {
  this_project: "this project",
  carrier_embargo: "carrier embargo",
  same_client: "same client",
  same_carrier: "same carrier",
  same_client_and_carrier: "same client and carrier",
};

// -----------------------------------------------------------------------------
// The one read every surface shares
// -----------------------------------------------------------------------------
export const OPEN_STANDDOWNS_KEY = ["hse_standdowns_open"] as const;

export function useOpenStanddowns(enabled = true) {
  return useQuery({
    queryKey: OPEN_STANDDOWNS_KEY,
    enabled,
    staleTime: 60_000,
    queryFn: async () => {
      const [s, f] = await Promise.all([
        (supabase as any).from("v_hse_standdowns").select("*").eq("is_open", true).order("date_from"),
        (supabase as any).from("v_hse_standdown_projects").select("standdown_id, project_id, relation, blocks"),
      ]);
      if (s.error) throw s.error;
      if (f.error) throw f.error;
      return { stops: (s.data ?? []) as Standdown[], flags: (f.data ?? []) as StandFlag[] };
    },
  });
}

export type ProjectStop = { stop: Standdown; relation: StandRelation; blocks: boolean };

/** Every open stop that touches a project — the blocking ones first. */
export function stopsForProject(data: { stops: Standdown[]; flags: StandFlag[] } | undefined, projectId: string | null | undefined): ProjectStop[] {
  if (!data || !projectId) return [];
  const byId = new Map(data.stops.map(s => [s.standdown_id, s]));
  const out: ProjectStop[] = [];
  for (const f of data.flags) {
    if (f.project_id !== projectId) continue;
    const stop = byId.get(f.standdown_id);
    if (stop) out.push({ stop, relation: f.relation, blocks: f.blocks });
  }
  return out.sort((a, b) => Number(b.blocks) - Number(a.blocks) || a.stop.date_from.localeCompare(b.stop.date_from));
}

/** The stop (if any) a booking's days fall inside. to = null means no finish date. */
export function stopCovering(data: { stops: Standdown[]; flags: StandFlag[] } | undefined, projectId: string | null | undefined, from: string, to: string | null): Standdown | null {
  for (const p of stopsForProject(data, projectId)) {
    if (!p.blocks) continue;
    const s = p.stop;
    if (s.date_from <= (to ?? "9999-12-31") && (s.block_to ?? "9999-12-31") >= from) return s;
  }
  return null;
}

/** True when the stop covers one particular day. */
export function stopOnDay(s: Standdown, day: string): boolean {
  return s.date_from <= day && (s.block_to ?? "9999-12-31") >= day;
}

// -----------------------------------------------------------------------------
// The banner — project page, field job page, site folder
// -----------------------------------------------------------------------------
// Red: work is stopped HERE (this project is stood down, or its carrier has an
// embargo). Amber: a sister site under the same client and/or carrier is
// stood down — a heads-up, bookings still go through. Nothing renders when
// there is nothing to say. `canOpen` turns the number into a link to the
// register (the office); the crew just read it.
export function ProjectStanddownBanner({ projectId, canOpen = false, className = "" }: { projectId: string | null | undefined; canOpen?: boolean; className?: string }) {
  const { data } = useOpenStanddowns(!!projectId);
  const mine = stopsForProject(data, projectId);
  if (mine.length === 0) return null;
  const today = localKey();
  return (
    <div className={`space-y-2 ${className}`}>
      {mine.map(({ stop: s, relation, blocks }) => {
        const now = stopOnDay(s, today);
        const num = canOpen
          ? <Link to={`/hse/standdowns/${s.standdown_id}`} className="font-mono underline underline-offset-2">{s.standdown_number}</Link>
          : <span className="font-mono">{s.standdown_number}</span>;
        if (blocks) {
          return (
            <div key={s.standdown_id} className="rounded-md border border-danger/50 bg-danger/10 px-4 py-3 flex items-start gap-3">
              <OctagonAlert size={18} className="text-danger shrink-0 mt-0.5"/>
              <div className="min-w-0 text-[13px] text-text">
                <div className="font-semibold text-danger">
                  {s.scope === "carrier"
                    ? `${s.carrier_name ?? "Carrier"} embargo${now ? " — no work on this site" : ` from ${fmtDay(s.date_from)}`}`
                    : now ? "STOOD DOWN — no work on this site" : `Stand-down from ${fmtDay(s.date_from)}`}
                </div>
                <div className="mt-0.5">
                  {num} · {blockedRange(s)} · stopped by {STOPPED_BY[s.stopped_by].toLowerCase()}{s.stopped_by_name ? ` (${s.stopped_by_name})` : ""}
                </div>
                <div className="text-text-muted mt-0.5">{s.reason}</div>
                {s.conditions_to_resume && <div className="text-text-muted mt-0.5">To resume: {s.conditions_to_resume}</div>}
                <div className="text-[12px] text-text-muted mt-1">
                  {now ? "Do not start work here. Call the office if you are on site or on your way." : "Nothing can be booked here inside those dates."}
                </div>
              </div>
            </div>
          );
        }
        return (
          <div key={s.standdown_id} className="rounded-md border border-warn/40 bg-warn/10 px-4 py-3 flex items-start gap-3">
            <TriangleAlert size={17} className="text-warn shrink-0 mt-0.5"/>
            <div className="min-w-0 text-[13px] text-text">
              <div className="font-semibold text-warn">
                {relation === "same_carrier" ? (s.carrier_name ?? "The carrier") : relation === "same_client" ? (s.client_name ?? "The client") : `${s.client_name ?? "The client"} / ${s.carrier_name ?? "carrier"}`}
                {" "}has a stop-work on {s.site_name ?? s.project_name ?? "another site"} since {fmtDay(s.date_from)}
              </div>
              <div className="mt-0.5">{num} · {RELATION_WORDS[relation]} · {s.reason}</div>
              <div className="text-[12px] text-text-muted mt-1">{canOpen
                ? "A warning, not a block — this site can still be booked. Check with the client before sending a crew."
                : "A heads-up only — work on this site carries on unless the office tells you otherwise."}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// -----------------------------------------------------------------------------
// The strip on the schedule — under "Public holidays coming up"
// -----------------------------------------------------------------------------
export function ScheduleStanddownStrip() {
  const { data } = useOpenStanddowns();
  const stops = data?.stops ?? [];
  if (stops.length === 0) return null;
  const today = localKey();
  return (
    <div className="mt-2 text-[11px] flex items-center gap-x-3 gap-y-1 flex-wrap rounded border border-danger/40 bg-danger/[0.07] px-2.5 py-1.5">
      <span className="inline-flex items-center gap-1.5 font-semibold text-danger"><OctagonAlert size={12}/> No bookings:</span>
      {stops.map(s => {
        const sisters = (data?.flags ?? []).filter(f => f.standdown_id === s.standdown_id && !f.blocks).length;
        return (
          <Link key={s.standdown_id} to={`/hse/standdowns/${s.standdown_id}`} className="inline-flex items-center gap-1.5 hover:underline"
            title={`${s.standdown_number} — ${s.reason}\nThe schedule refuses new bookings on ${s.scope === "carrier" ? "every project under this carrier" : "this project"} inside these dates.`}>
            <span className="font-medium text-text">{s.scope === "carrier" ? `${s.carrier_name ?? "Carrier"} embargo` : `${s.site_name ?? s.project_name ?? s.project_code} stood down`}</span>
            <span className="font-mono text-text-muted">{blockedRange(s)}</span>
            {!stopOnDay(s, today) && <span className="text-warn">(coming up)</span>}
            {sisters > 0 && <span className="text-warn">· {sisters} sister site{sisters === 1 ? "" : "s"} flagged</span>}
          </Link>
        );
      })}
    </div>
  );
}

// -----------------------------------------------------------------------------
// The stops DRAWN on the schedule (8 Oct 2026, same day as mig 425)
// -----------------------------------------------------------------------------
// James, in the first smoke: "can we make Embargos visually on the schedule
// page where we work … it's really where we do all our planning with the
// teams. So either there or show it in a colour in the calendar tab as that's
// more visual, could also show the stop works in a bar so people can see it."
//
//   <StopsLane>          Workforce / People / Gantt — a lane directly under the
//                        date ruler, one red hatched bar per stand-down or
//                        embargo across the days it blocks, on the same day
//                        grid as the crew rows below it. An open-ended
//                        stand-down runs off the right edge.
//   <CalendarWeekStops>  Calendar tab — the same bars along the bottom of each
//                        week row of the month grid.
// Both render nothing when no stop touches the days on screen, and each bar is
// a link to the stand-down. They are drawings only — the refusal is still the
// database's.
const HATCH = "repeating-linear-gradient(135deg, rgba(220,38,38,0.92) 0 7px, rgba(185,28,28,0.92) 7px 14px)";

function keyToDate(k: string): Date { const [y, m, d] = k.slice(0, 10).split("-").map(Number); return new Date(y, m - 1, d); }
function dayDiff(fromKey: string, toKey: string): number { return Math.round((keyToDate(toKey).getTime() - keyToDate(fromKey).getTime()) / 86400000); }
function addDaysKey(k: string, n: number): string { const d = keyToDate(k); d.setDate(d.getDate() + n); return localKey(d); }

/** What a stop is called on a bar. */
export function stopBarLabel(s: Standdown): string {
  return s.scope === "carrier"
    ? `${s.carrier_name ?? "Carrier"} embargo`
    : `${s.site_name ?? s.project_name ?? s.project_code ?? "Site"} stood down`;
}
function stopBarTitle(s: Standdown): string {
  return [
    `${s.standdown_number} — ${stopBarLabel(s)}`,
    blockedRange(s),
    s.reason,
    s.scope === "carrier" ? "No work can be booked on any project under this carrier inside these dates." : "No work can be booked on this project until it is cleared.",
    "Click to open it.",
  ].join("\n");
}

/** The part of a stop that falls inside [winFrom, winFrom + days - 1], as day offsets — or null. */
function clipStop(s: Standdown, winFrom: string, days: number): { start: number; end: number; runsOn: boolean; startsBefore: boolean } | null {
  const winTo = addDaysKey(winFrom, days - 1);
  if (s.date_from > winTo) return null;
  if (s.block_to && s.block_to < winFrom) return null;
  const start = Math.max(0, dayDiff(winFrom, s.date_from));
  const end = s.block_to ? Math.min(days - 1, dayDiff(winFrom, s.block_to)) : days - 1;
  if (end < start) return null;
  return { start, end, runsOn: !s.block_to || s.block_to > winTo, startsBefore: s.date_from < winFrom };
}

export function StopsLane({ gridStartKey, totalDays, labelCol }: { gridStartKey: string; totalDays: number; labelCol: string }) {
  const { data } = useOpenStanddowns();
  const rows = (data?.stops ?? [])
    .map(s => ({ s, c: clipStop(s, gridStartKey, totalDays) }))
    .filter((r): r is { s: Standdown; c: NonNullable<ReturnType<typeof clipStop>> } => r.c !== null);
  if (rows.length === 0) return null;
  const LANE = 22;
  return (
    <div className="grid items-center px-3 py-1.5 border-b border-danger/30 bg-danger/[0.05]"
      style={{ gridTemplateColumns: `${labelCol} repeat(${totalDays}, minmax(8px, 1fr))` }}>
      <div className="min-w-0 pr-3 flex items-center gap-1.5 text-[11px] font-semibold text-danger">
        <OctagonAlert size={13}/> <span className="truncate">No bookings</span>
      </div>
      <div className="relative" style={{ gridColumn: "2 / -1", height: rows.length * LANE - 2 }}>
        {rows.map(({ s, c }, i) => (
          <Link key={s.standdown_id} to={`/hse/standdowns/${s.standdown_id}`} title={stopBarTitle(s)}
            className="absolute flex items-center px-2 text-[10px] font-semibold text-white truncate hover:brightness-110"
            style={{
              top: i * LANE, height: LANE - 2,
              left: `${(c.start / totalDays) * 100}%`,
              width: `${((c.end - c.start + 1) / totalDays) * 100}%`,
              background: HATCH,
              borderRadius: `${c.startsBefore ? 0 : 4}px ${c.runsOn ? 0 : 4}px ${c.runsOn ? 0 : 4}px ${c.startsBefore ? 0 : 4}px`,
            }}>
            <span className="truncate" style={{ textShadow: "0 1px 1px rgba(0,0,0,0.45)" }}>
              ⛔ {stopBarLabel(s)}{s.block_to ? "" : " — until cleared"}
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
}

/** Calendar tab: stops along the bottom of one week row (weekStartKey = that row's Monday). */
export function CalendarWeekStops({ weekStartKey }: { weekStartKey: string }) {
  const { data } = useOpenStanddowns();
  const rows = (data?.stops ?? [])
    .map(s => ({ s, c: clipStop(s, weekStartKey, 7) }))
    .filter((r): r is { s: Standdown; c: NonNullable<ReturnType<typeof clipStop>> } => r.c !== null)
    .slice(0, 3);
  if (rows.length === 0) return null;
  const LANE = 16;
  return (
    <div className="absolute left-0 right-0 bottom-0 pointer-events-none" style={{ height: rows.length * LANE }}>
      {rows.map(({ s, c }, i) => (
        <Link key={s.standdown_id} to={`/hse/standdowns/${s.standdown_id}`} title={stopBarTitle(s)}
          className="absolute flex items-center px-1.5 text-[9px] font-semibold text-white truncate pointer-events-auto hover:brightness-110"
          style={{
            bottom: i * LANE, height: LANE - 2,
            left: `${(c.start / 7) * 100}%`,
            width: `${((c.end - c.start + 1) / 7) * 100}%`,
            background: HATCH,
            borderRadius: `${c.startsBefore ? 0 : 3}px ${c.runsOn ? 0 : 3}px ${c.runsOn ? 0 : 3}px ${c.startsBefore ? 0 : 3}px`,
          }}>
          <span className="truncate" style={{ textShadow: "0 1px 1px rgba(0,0,0,0.45)" }}>⛔ {stopBarLabel(s)}</span>
        </Link>
      ))}
    </div>
  );
}
